// src/pages/ShopPage.js
// eslint-disable-next-line no-unused-vars
import React, {useState, useEffect, useMemo} from 'react';
import styled from 'styled-components';
import PageContentComponent from '../components/ui/pageContentComponent';
import PageContainerTitleComponent from '../components/ui/container/pageContainerTitleComponent';
import {TopBar} from '../components/ui/shop/topBar';
import {CategoryList} from '../components/ui/shop/categoryList';
import ItemGrid from '../components/ui/shop/itemGrid'; // Default export
import PurchaseConfirmationModal from '../components/ui/shop/purchaseConfirmationModal';
import PurchaseResultModal from '../components/ui/shop/purchaseResultModal';
import {getShopItems, purchaseItem, getActiveServers} from '../api/axios';
import ServerSwitch from '../components/ui/serverSwitch';
import {useAuth} from '../hooks/useAuth';
import {useTranslation} from 'react-i18next';

const ShopPage = () => {
  // Alle States unbedingungsgemäß definieren
  const {t} = useTranslation();
  const {user, refreshUserData} = useAuth();
  const [arcanium, setArcanium] = useState(user?.cash_points || 0);
  const [votePoints, setVotePoints] = useState(user?.vote_points || 0);
  const [arc_hybrid, setArcHybrid] = useState(user?.arc_hybrid || 0);
  const [arc_pvp, setArcPvp] = useState(user?.arc_pvp || 0);
  const [arc_saison, setArcSaison] = useState(user?.arc_saison || 0);
  const [currencyMode, setCurrencyMode] = useState('arcanium');
  const [itemsPerPage, setItemsPerPage] = useState(9);
  const [shopItems, setShopItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [purchaseConfirmation, setPurchaseConfirmation] = useState(null);
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [purchaseResult, setPurchaseResult] = useState(null);

  const [sv, setSv] = useState('');
  const [activeServers, setActiveServers] = useState([]);
  const showServerSwitch = activeServers.length > 1;

  const [searchQuery, setSearchQuery] = useState('');

  // Alle Hooks ganz oben:

  useEffect(() => {
    let mounted = true;
    getActiveServers()
      .then(res => {
        if (!mounted) return;
        const list = Array.isArray(res?.data?.servers) ? res.data.servers : [];
        setActiveServers(list);
        // genau 1 aktiv -> diesen setzen
        if (list.length === 1 && list[0]?.friendly_key) setSv(list[0].friendly_key);
        // mehrere aktiv -> User muss bewusst einen Server auswählen
        if (list.length > 1 && sv && !list.find(s => s.friendly_key === sv)) {
          setSv('');
        }
      })
      .catch(() => setActiveServers([]));
    return () => {
      mounted = false;
    };
  }, [sv]);

  // Laden der Shop-Items
  useEffect(() => {
    const timer = setTimeout(() => {
      getShopItems()
        .then(response => {
          setShopItems(response.data);
          setLoading(false);
        })
        .catch(error => {
          setLoading(false);
        });
    }, 500); // 500ms Delay

    return () => clearTimeout(timer);
  }, []);

  // Aktualisieren des Guthabens
  useEffect(() => {
    setArcanium(user?.cash_points || 0);
    setVotePoints(user?.vote_points || 0);
    setArcHybrid(user?.arc_hybrid || 0);
    setArcPvp(user?.arc_pvp || 0);
    setArcSaison(user?.arc_saison || 0);
  }, [user]);

  // Responsive Items per Page
  useEffect(() => {
    const handleResize = () => {
      setItemsPerPage(window.innerWidth < 490 ? 8 : 9);
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Bei Kategoriewechsel zurück auf Seite 1
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  // Berechnung von filteredItems und totalPages in der Komponentenkörper
  const filteredItems = useMemo(() => {
    const q = (searchQuery || '').trim().toLowerCase();
    const isSearchActive = q.length >= 3;

    const allowedByCurrency = item => {
      // 1 = Arc-only, 2 = VP-only, 3 = beide
      if (item.shop_type === 3) return true;
      if (item.shop_type === 1 && currencyMode === 'arcanium') return true;
      if (item.shop_type === 2 && currencyMode === 'votePoints') return true;
      return false;
    };

    const nameMatches = item => {
      const name = String(item.display_name || item.name || item.item_name || item.title || '').toLowerCase();
      return name.includes(q);
    };

    if (isSearchActive) {
      // Suche: Kategorien ignorieren, aber Währungs-/shop_type-Regel EINHALTEN
      return shopItems.filter(item => allowedByCurrency(item) && nameMatches(item));
    }

    // Standard: Kategorie + Währungs-/shop_type-Regel
    return shopItems.filter(item => item.category === selectedCategory && allowedByCurrency(item));
  }, [shopItems, selectedCategory, currencyMode, searchQuery]);

  const totalPages = filteredItems.length > 0 ? Math.ceil(filteredItems.length / itemsPerPage) : 0;

  // useEffect, um currentPage an Grenzen anzupassen
  useEffect(() => {
    if (totalPages === 0) {
      setCurrentPage(0);
    } else if (currentPage < 1) {
      setCurrentPage(1);
    } else if (currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [totalPages, currentPage]);

  let indexOfLastItem = currentPage * itemsPerPage;
  let indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems =
    totalPages > 0 && currentPage > 0
      ? filteredItems.slice(indexOfFirstItem, indexOfLastItem).map(item => ({
          ...item,
          icon: `/assets/items/${item.item_icon ? item.item_icon : 'unknown'}.jpg`,
        }))
      : [];

  const arcMap = {
    hybrid: arc_hybrid,
    pvp: arc_pvp,
    saison: arc_saison,
  };

  const svKey = (sv || 'hybrid').toLowerCase();
  const selectedServerArc = arcMap[svKey] ?? arc_hybrid;

  const currentBalance = currencyMode === 'arcanium' ? arcanium + selectedServerArc : votePoints;

  const isSearchActive = (searchQuery || '').trim().length >= 3;

  // Kauf-Logik
  const handleBuy = item => {
    if (showServerSwitch && !sv) {
      setPurchaseResult({success: false, message: 'Please select a server first.'});
      return;
    }

    const basePrice =
      item.shop_type === 1
        ? item.arcanium_price
        : item.shop_type === 2
        ? item.vote_price
        : currencyMode === 'arcanium'
        ? item.arcanium_price
        : item.vote_price;

    let price = basePrice;
    if (item.event_active && Number(item.discount) > 0) {
      price = Math.round(basePrice - (basePrice * item.discount) / 100);
    }

    if (price > currentBalance) {
      //alert(`Not enough ${currencyMode === 'arcanium' ? 'Arcanium' : 'Vote Points'}!`);
      return;
    }
    setPurchaseConfirmation({item, price});
  };

  const confirmPurchase = async () => {
    if (showServerSwitch && !sv) {
      setPurchaseResult({success: false, message: 'Please select a server first.'});
      setPurchaseConfirmation(null);
      return;
    }

    setPurchaseLoading(true);
    try {
      const payload = {
        product_id: purchaseConfirmation.item.product_id,
        currency_mode: currencyMode,
        sv: sv,
      };
      const response = await purchaseItem(payload);
      if (response.data.success) {
        setPurchaseResult({success: true, message: response.data.message});
        // Jetzt User-Daten neu laden
        await refreshUserData();
      } else {
        setPurchaseResult({success: false, message: response.data.error || 'Purchase failed'});
      }
    } catch (error) {
      setPurchaseResult({success: false, message: 'Purchase failed: ' + error.message});
    } finally {
      setPurchaseLoading(false);
      setPurchaseConfirmation(null);
    }
  };

  const cancelPurchase = () => setPurchaseConfirmation(null);
  const handleCloseResult = () => setPurchaseResult(null);

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const pageIndicatorText = totalPages === 0 ? '0/0' : `${currentPage}/${totalPages}`;

  return (
    <PageContentComponent>
      <PageContainerTitleComponent title={t('shopPage.title')} />
      {showServerSwitch && (
        <div style={{display: 'flex', justifyContent: 'flex-end', margin: '8px 0 4px'}}>
          <ServerSwitch
            value={sv}
            onChange={setSv}
            label={t('shopPage.server')}
            // optional: nur aktive als Optionen übergeben
            options={activeServers.map(s => ({value: s.friendly_key, label: s.display_name || s.friendly_key}))}
          />
        </div>
      )}
      <CanvasContainer>
        <TopBar
          currencyMode={currencyMode}
          setCurrencyMode={setCurrencyMode}
          arcanium={arcanium}
          votePoints={votePoints}
          arcHybrid={arc_hybrid}
          arcPvp={arc_pvp}
          arcSaison={arc_saison}
          server={sv}
        />

        <MainSection>
          {!isSearchActive && (
            <CategoryList
              categories={[
                t('shopPage.categories.sales'),
                t('shopPage.categories.functional'),
                t('shopPage.categories.soulStones'),
                t('shopPage.categories.consumables'),
                t('shopPage.categories.creatures'),
                t('shopPage.categories.decoration'),
                t('shopPage.categories.equipment'),
                t('shopPage.categories.pets'),
              ]}
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
            />
          )}
          <SearchContainer>
            <SearchInput
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder={t('shopPage.search')}
            />
            {isSearchActive && <SearchDeleteButton onClick={() => setSearchQuery('')}>✕</SearchDeleteButton>}
          </SearchContainer>
          {loading ? (
            <ItemAreaLoading>
              <SpinnerOverlayShop>
                <Spinner />
              </SpinnerOverlayShop>
            </ItemAreaLoading>
          ) : (
            <ItemArea>
              <ItemGrid
                items={currentItems}
                currencyMode={currencyMode}
                handleBuy={handleBuy}
                currentBalance={currentBalance}
              />
              <PaginationContainer>
                <PageButton onClick={handlePrevPage} disabled={currentPage <= 1 || totalPages === 0}>
                  {'<'}
                </PageButton>
                <PageIndicator>{pageIndicatorText}</PageIndicator>
                <PageButton onClick={handleNextPage} disabled={currentPage >= totalPages || totalPages === 0}>
                  {'>'}
                </PageButton>
              </PaginationContainer>
            </ItemArea>
          )}
        </MainSection>
      </CanvasContainer>

      {/* Kaufbestätigungs-Modal */}
      {purchaseConfirmation && (
        <PurchaseConfirmationModal
          item={purchaseConfirmation.item}
          price={purchaseConfirmation.price}
          currencyMode={currencyMode}
          onCancel={cancelPurchase}
          onConfirm={confirmPurchase}
        />
      )}

      {/* Spinner Overlay beim Kaufen */}
      {purchaseLoading && (
        <SpinnerOverlay>
          <Spinner />
        </SpinnerOverlay>
      )}

      {/* Ergebnis-Modal */}
      {purchaseResult && (
        <PurchaseResultModal
          show={!!purchaseResult}
          onHide={handleCloseResult}
          success={purchaseResult.success}
          message={purchaseResult.message}
        />
      )}
    </PageContentComponent>
  );
};

export default ShopPage;

// =========== Styled Components ===========
const CanvasContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const MainSection = styled.div`
  display: flex;
  flex: 1;
  width: 100%;
  flex-direction: column;
`;

const ItemAreaLoading = styled.div`
  flex: 1;
  padding: 10px;
  display: flex;
  flex-direction: column;
  min-height: 590px;
  position: relative;
`;

const ItemArea = styled.div`
  flex: 1;
  padding: 10px;
  display: flex;
  flex-direction: column;
  min-height: 590px;
  position: relative;
`;

const PaginationContainer = styled.div`
  margin-top: auto;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
`;

const PageButton = styled.button`
  border: none;
  padding: 6px 10px;
  cursor: pointer;
  border-radius: 4px;
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SearchContainer = styled.div`
  margin-top: auto;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
`;

const SearchDeleteButton = styled.button`
  border: none;
  padding: 6px 10px;
  height: auto;
  width: 70px;
  cursor: pointer;
  border-radius: 4px;
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SearchInput = styled.input`
  padding: 10px 10px 10px 40px;
  border: 1px solid #ccc;
  border-radius: 5px;
  width: 100%;
`;

const PageIndicator = styled.span`
  font-weight: bold;
`;

const Spinner = styled.div`
  border: 4px solid #f3f3f3;
  border-top: 4px solid #007bff;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const SpinnerOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
`;

const SpinnerOverlayShop = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
`;
